import * as React from 'react';
import { css } from '@emotion/core';
import { COLORS } from '../globalStyle/colors';
import { useAppState } from '../core/redux/hooks';
import { TextDirection } from '../localization/textDirection';


const styles = {
  root: css`
    table-layout: auto;
    width: 100%;
    border-collapse: collapse;
    color: ${COLORS.DARK_GREY};
    background-color: ${COLORS.LIGHTEST_GREY};

    & th, & td {
      padding: 18px 12px;
      border: 1px solid;
      border-color: ${COLORS.LIGHTER_GREY} ${COLORS.LIGHTEST_GREY};
    }

    & th {
      font-size: 13px;
    }
  `,
};


type Props = {
  className?: string;
  headers: React.ReactNode[];
}

/**
 * DataTable: a component that renders a table using the provided header elements (mapped according to their order in
 * the array). The table is setup with the styling common to all table elements (including for data rows), but this can
 * be overridden as needed.
 *
 * Note that you're still responsible for constructing your own data rows.
 *
 * PureComponent: true
 * Redux Connected: false
 */
const DataTable: React.FC<Props> = props => {
  const { headers, className } = props;
  const dir        = useAppState(state => state.locale.textDirection);
  const isRtl      = React.useMemo(() => dir === TextDirection.RTL, [dir]);

  // Include text direction on the headers so that we can pad appropriately
  const mapHeaders = React.useMemo(
    () => headers.map(header => <th dir={dir}>{header}</th>),
    [dir, headers],
  );

  return (
    <table className={className} css={styles.root}>
      <thead css={css`text-align: ${isRtl ? 'right' : 'left'};`}>
        <tr>
          {mapHeaders}
        </tr>
      </thead>
      <tbody>
        {props.children}
      </tbody>
    </table>
  );
};

export default DataTable;
